// ABOUTME: Import panel for the bulk import/export modal — validate a multi-entity bundle, then import.
import {Check, WarningAmber} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material'
import React, {ChangeEvent, useState} from 'react'
import {useSnackbar} from 'notistack'

import {useTranslation} from '../../../../hooks/useTranslation'
import {
  BulkCompatibilityReport,
  BulkEntityImportResult,
  BulkImportReport,
  useBulkImport,
  useBulkValidateImport
} from '../hooks/useConfigImportExport'

interface BulkImportPanelProps {
  countryId: string
  businessLine: string
}

type Translate = (key: string, opts?: Record<string, unknown>) => string

const compatSecondary = (issues: string[]): string | undefined =>
  issues.length > 0 ? issues.join(' ') : undefined

const resultSecondary = (entity: BulkEntityImportResult, t: Translate): string => {
  if (entity.error) return entity.error
  if (!entity.accepted) return t('countryConfigurations.importExport.bulk.skipped')
  return t('countryConfigurations.importExport.bulk.entityResult', {
    created: entity.report?.created ?? 0,
    updated: entity.report?.updated ?? 0,
    failed: entity.report?.failed ?? 0
  })
}

const BulkCompatList: React.FC<{compat: BulkCompatibilityReport}> = ({compat}) => {
  return (
    <List dense>
      {compat.entities.map((entity) => (
        <ListItem key={entity.entityKey} disablePadding>
          <ListItemText primary={entity.entityKey} secondary={compatSecondary(entity.issues)} />
          {entity.compatible ? (
            <Check color="success" fontSize="small" />
          ) : (
            <WarningAmber color="warning" fontSize="small" />
          )}
        </ListItem>
      ))}
    </List>
  )
}

const BulkResultList: React.FC<{report: BulkImportReport}> = ({report}) => {
  const {t} = useTranslation()
  return (
    <List dense>
      {report.entities.map((entity) => (
        <ListItem key={entity.entityKey} disablePadding>
          <ListItemText primary={entity.entityKey} secondary={resultSecondary(entity, t)} />
        </ListItem>
      ))}
    </List>
  )
}

export const BulkImportPanel: React.FC<BulkImportPanelProps> = ({countryId, businessLine}) => {
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()
  const validate = useBulkValidateImport()
  const bulkImport = useBulkImport(countryId, businessLine)

  const [fileName, setFileName] = useState<string | null>(null)
  const [bundle, setBundle] = useState<unknown>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [compat, setCompat] = useState<BulkCompatibilityReport | null>(null)
  const [report, setReport] = useState<BulkImportReport | null>(null)
  const [force, setForce] = useState(false)

  const reset = () => {
    setBundle(null)
    setParseError(null)
    setCompat(null)
    setReport(null)
    setForce(false)
  }

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    reset()
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setFileName(file.name)
    try {
      const parsed = JSON.parse(await file.text())
      setBundle(parsed)
      setCompat(await validate.mutateAsync(parsed))
    } catch {
      setParseError(t('countryConfigurations.importExport.importDialog.invalidFile'))
    }
  }

  const runImport = async () => {
    if (bundle === null) return
    try {
      const result = await bulkImport.mutateAsync({bundle, force})
      setReport(result)
      const hasProblem = result.entities.some((entity) => !entity.accepted || (entity.report?.failed ?? 0) > 0)
      enqueueSnackbar(
        hasProblem
          ? t('countryConfigurations.importExport.bulk.importPartial')
          : t('countryConfigurations.importExport.bulk.importSuccess'),
        {variant: hasProblem ? 'warning' : 'success', anchorOrigin: {vertical: 'top', horizontal: 'center'}}
      )
    } catch {
      enqueueSnackbar(t('countryConfigurations.importExport.importDialog.error'), {
        variant: 'error',
        anchorOrigin: {vertical: 'top', horizontal: 'center'}
      })
    }
  }

  const hasIncompatible = compat?.entities.some((entity) => !entity.compatible) ?? false
  const busy = validate.isPending || bulkImport.isPending

  if (report) {
    return <BulkResultList report={report} />
  }

  return (
    <>
      <Button component="label" variant="outlined" disabled={busy}>
        {t('countryConfigurations.importExport.importDialog.chooseFile')}
        <input type="file" accept=".json,application/json" hidden onChange={handleFile} />
      </Button>
      {fileName && (
        <Typography variant="body2" sx={{mt: 1}}>
          {fileName}
        </Typography>
      )}
      {parseError && (
        <Alert severity="error" sx={{mt: 2}}>
          {parseError}
        </Alert>
      )}
      {compat && <BulkCompatList compat={compat} />}
      {hasIncompatible && (
        <FormControlLabel
          control={<Checkbox checked={force} onChange={(e) => setForce(e.target.checked)} />}
          label={t('countryConfigurations.importExport.bulk.forceLabel')}
        />
      )}
      {compat && (
        <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
          <Button variant="contained" disabled={busy} onClick={runImport}>
            {t('countryConfigurations.importExport.bulk.importButton')}
          </Button>
        </Box>
      )}
    </>
  )
}
